import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import LoginPage from './pages/Auth/LoginPage';
import { AnimatedBackground } from './components/AnimatedBackground';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout/Layout';
import { APP_VERSION, DEPLOY_TIME, BUILD_NUMBER } from './config/version';
import buildInfo from './buildInfo.json';
import { LanguageProvider } from './contexts/LanguageContext';
import { apiService } from './services/api';
import ProtectedRoute from './components/ProtectedRoute';
import { initializeLocalStorageData } from './utils/restoreLocalStorageData';
import { clearAllCaches } from './utils/clearCache';
import './utils/recalculateStreaks';

// Lazy load pages for better performance
const DashboardPage = lazy(() => import('./pages/Dashboard/DashboardPage'));
const ProjectsPage = lazy(() => import('./pages/Projects/ProjectsPage'));
const ProjectDetailsPage = lazy(() => import('./pages/Projects/ProjectDetailsPage'));
const TasksPage = lazy(() => import('./pages/Tasks/TasksPage'));
const TeamsPage = lazy(() => import('./pages/Teams/TeamsPage'));
const CrmPage = lazy(() => import('./pages/CRM/CrmPage'));
const TimeTrackingPage = lazy(() => import('./pages/TimeTracking/TimeTrackingPage'));
const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'));
const RequestsPage = lazy(() => import('./pages/Requests/RequestsPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagement/UserManagementPage'));
const SalesPage = lazy(() => import('./pages/Sales/SalesPage'));
const SourcingPage = lazy(() => import('./pages/Sourcing/SourcingPage'));
const ResearchPageWithTabs = lazy(() => import('./pages/Research/ResearchPageWithTabs'));

function App() {
  const { isAuthenticated } = useAuthStore();
  
  // Deployment verification
  console.log(`🚀 Group Legion Panel v${buildInfo.version} deployed successfully!`);
  console.log('✅ All features enabled');
  console.log('📅 Deployment time:', new Date().toISOString());

  useEffect(() => {
    const CURRENT_VERSION = 'v3.0.0-FIXED-TASKS';
    console.log(`%c🚀 Group Legion Panel ${CURRENT_VERSION}`, 'color: #4CAF50; font-size: 16px; font-weight: bold');
    console.log(`📅 Build Time: ${buildInfo.buildTime}`);
    console.log(`🔨 Timestamp: ${buildInfo.timestamp}`);
    console.log(`📌 Commit: ${buildInfo.commitHash}`);
    console.log('✅ Build info loaded from buildInfo.json');
    console.log('🔧 All fixes applied - TASKS DIALOG FIXED');
    
    // Check if version changed and clear cache
    const lastVersion = localStorage.getItem('app_version');
    if (lastVersion !== CURRENT_VERSION) {
      console.log('Version changed, clearing all caches...');
      clearAllCaches();
      localStorage.setItem('app_version', CURRENT_VERSION);
    }
    
    // Initialize localStorage data if missing
    // DISABLED: This was overwriting real data with dummy data
    // initializeLocalStorageData();
    
    // Add version to window title
    document.title = `Group Legion Panel v${buildInfo.version}`;
    
    // Perform API health check
    apiService.checkHealth().catch(error => {
      console.error('⚠️ API Health check failed. Backend may be unavailable.');
    });
  }, []);

  if (!isAuthenticated) {
    return (
      <LanguageProvider>
        <Box sx={{ position: 'relative', minHeight: '100vh' }}>
          <AnimatedBackground />
          <Routes>
            <Route path="/auth/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/auth/login" replace />} />
          </Routes>
        </Box>
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
      <Layout>
        <Suspense fallback={
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
          </Box>
        }>
          <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/projects" element={
            <ProtectedRoute resource="projects">
              <ProjectsPage />
            </ProtectedRoute>
          } />
          <Route path="/projects/:id" element={
            <ProtectedRoute resource="projects">
              <ProjectDetailsPage />
            </ProtectedRoute>
          } />
          <Route path="/tasks" element={
            <ProtectedRoute resource="tasks">
              <TasksPage />
            </ProtectedRoute>
          } />
          <Route path="/teams" element={
            <ProtectedRoute resource="teams">
              <TeamsPage />
            </ProtectedRoute>
          } />
          <Route path="/crm" element={
            <ProtectedRoute resource="crm">
              <CrmPage />
            </ProtectedRoute>
          } />
          <Route path="/time-tracking" element={
            <ProtectedRoute resource="timeTracking">
              <TimeTrackingPage />
            </ProtectedRoute>
          } />
          <Route path="/requests" element={
            <ProtectedRoute resource="requests">
              <RequestsPage />
            </ProtectedRoute>
          } />
          <Route path="/sales" element={
            <ProtectedRoute resource="sales">
              <SalesPage />
            </ProtectedRoute>
          } />
          <Route path="/sourcing" element={
            <ProtectedRoute resource="sourcing">
              <SourcingPage />
            </ProtectedRoute>
          } />
          <Route path="/research" element={
            <ProtectedRoute resource="research">
              <ResearchPageWithTabs />
            </ProtectedRoute>
          } />
          <Route path="/research-new" element={
            <ProtectedRoute resource="research">
              <ResearchPageWithTabs />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/users" element={
            <ProtectedRoute resource="users">
              <UserManagementPage />
            </ProtectedRoute>
          } />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </Suspense>
      </Layout>
    </LanguageProvider>
  );
}

export default App;// Force rebuild Mon Jul 21 20:23:25 EEST 2025
